import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useQueryParams } from '@fs/zion-router'
import {
  Button,
  colors,
  Divider,
  FlowGrid,
  Image,
  LayoutBand,
  Paragraph,
  Radio,
  RadioGroup,
  Row,
  Separator,
  useContainerWidth,
} from '@fs/zion-ui'
import { ControlGrid, Menu, MenuSearch, NoticeQuestion, Person, PlaceHome, PlaceWorld } from '@fs/zion-icon'
import { getTryDemoLcrCalling } from '../../../lib/tryDemoServices'
import { useNavigate } from '../../../hooks'
import lightRaySvg from '../../../images/LcrDemo/lightRayBlue.svg'
import ChristHeaderLogoSvg from '../../../images/LcrDemo/ChristHeaderLogo.png'
import LcrNavBarItemMobile from './LcrNavBarItemMobile'
import LcrNavBarItem from './LcrNavBarItem'

const logoHeight = 48
const lcrMenuColor = '#006184'

// #region Styles
const cssBottomShadowBorder = css`
  box-shadow: 0 4px 4px #00000055;
  height: 5px;
  width: 100%;
`
const cssDefaultCursor = css`
  cursor: default;
`
const cssNavBarLeftMargin = css`
  margin-inline-start: 12px;
`
const cssHeaderImagePlacement = css`
  display: flex;
`
const cssLcrLabel = css`
  height: 100%;
  align-content: center;
`
const cssUberSearchBar = css`
  display: flex;

  & > button {
    flex-shrink: 0;
  }
`
const cssUberSearchTextField = css`
  max-width: 400px;
  width: 100%;
  padding: 9px;
`
// #endregion

/**
 * @describe - Display the LCR header.
 */
export default function LcrHeader() {
  const [t] = useTranslation()
  const atSize = useContainerWidth()
  const isMobile = atSize({ default: true, xxl: false })
  const showHeaderHamburgerMenu = atSize({ default: true, xs: false })
  const centerUnitInfo = atSize({ default: true, lg: false })
  const { location } = useNavigate()
  const { query } = useQueryParams()
  const [lcrCalling, setLcrCalling] = useState('bishop')
  const isBishop = lcrCalling === 'bishop'
  const isLcrDashboard = useMemo(() => location.pathname.includes('/lcr-demo/dashboard'), [location.pathname])

  useEffect(() => {
    getTryDemoLcrCalling().then(setLcrCalling)
  }, [query.calling])

  const [selectedRadio, setselectedRadio] = useState('my ward')
  const handleRadioChange = useCallback((event) => {
    setselectedRadio(event.target.value)
  }, [])

  // #region Translations
  const lcrHeaderLabel = t('lcr.navbar.label', 'Leader and Clerk Resources')
  const templeSubMenuItems = [
    {
      dataTestId: 'menu-item.fna',
      primaryText: t(
        'lcr.navbar.item.temple.dropdown.item1',
        'Family Name Assist: Help a Member Print a Family Name Card'
      ),
      to: !isBishop && '/lcr-demo/fna',
    },
  ]
  if (isBishop) {
    templeSubMenuItems.push(
      { primaryText: t('lcr.navbar.item.temple.dropdown.item2', 'Limited-Use Recommend for Sealings') },
      { primaryText: t('lcr.navbar.item.temple.dropdown.item3', 'Recommend Activations') },
      { primaryText: t('lcr.navbar.item.temple.dropdown.item4', 'Recommend Temple Workers') },
      {
        dataTestId: 'menu-item.temple-recommend',
        primaryText: t('lcr.navbar.item.temple.dropdown.item5', 'Temple Recommends'),
        to: '/lcr-demo/temple-recommend',
      }
    )
  }
  templeSubMenuItems.push(
    { primaryText: t('lcr.navbar.item.temple.dropdown.section-heading', 'Reports'), isHeading: true },
    { primaryText: t('lcr.navbar.item.temple.dropdown.item6', 'Family History Activity Report') }
  )
  if (isBishop) {
    templeSubMenuItems.push(
      { primaryText: t('lcr.navbar.item.temple.dropdown.item7', 'Not Endowed') },
      { primaryText: t('lcr.navbar.item.temple.dropdown.item8', 'Temple Recommend Status') },
      { primaryText: t('lcr.navbar.item.temple.dropdown.item9', 'Without Recommend') },
      { primaryText: t('lcr.navbar.item.temple.dropdown.item10', 'Youth Recommend Status') }
    )
  }
  const menuOptions = [
    { menuText: t('lcr.navbar.item.membership.label', 'Membership') },
    { menuText: t('lcr.navbar.item.callings.label', 'Callings') },
    { menuText: t('lcr.navbar.item.confidential.label', 'Confidential') },
    { menuText: t('lcr.navbar.item.ministering-and-welfare.label', 'Ministering and Welfare') },
    { menuText: t('lcr.navbar.item.finance.label', 'Finance') },
    { menuText: t('lcr.navbar.item.missionary.label', 'Missionary') },
    {
      menuText: t('lcr.navbar.item.temple.label', 'Temple'),
      showGlowMenu: true,
    },
    { menuText: t('lcr.navbar.item.reports.label', 'Reports') },
    { menuText: t('lcr.navbar.item.help.label', 'Help') },
  ]

  // ÜberSearch
  const searchFieldPlaceholder = t('lcr.search-bar.placeholder', 'Find members, reports and forms')
  const searchButtonText = t('lcr.search-button.text', 'Search')
  const searchRadio1Label = t('lcr.search.radio1.label', 'My Ward')
  const searchRadio2Label = t('lcr.search.radio2.label', 'Other Units and Leaders')

  // Stake/Branch info
  const stakeUnit = t('lcr.stake-unit', '{stakeName} Stake ({id})', { stakeName: 'ICS', id: '13012' })
  const branchUnit = t('lcr.branch-unit', '{branchName} Branch ({id})', {
    branchName: 'Riverton Office',
    id: '102345',
  })
  // #endregion

  return (
    <>
      <LayoutBand maxWidth="xxl" marginX="none">
        <Row gutters="xxs">
          <div css={cssHeaderImagePlacement}>
            <Image
              src={ChristHeaderLogoSvg}
              alt=""
              height={logoHeight}
              width={logoHeight}
              noCrop
              style={{ backgroundColor: lcrMenuColor, padding: '4px 0 ' }}
            />
            <Image
              positionX="left"
              src={lightRaySvg}
              alt=""
              width={logoHeight} // rotated -90deg, the height becomes the width
              style={{ transform: 'rotate(-90deg)' }}
            />
          </div>
          <div css={cssLcrLabel} growX>
            <Paragraph size="sm">{lcrHeaderLabel}</Paragraph>
          </div>
          {showHeaderHamburgerMenu && <Menu alignY="middle" />}
          {!showHeaderHamburgerMenu && <PlaceWorld alignY="middle" />}
          {!showHeaderHamburgerMenu && <ControlGrid alignY="middle" />}
          {!showHeaderHamburgerMenu && <Person alignY="middle" />}
        </Row>
      </LayoutBand>
      <Divider />
      <LayoutBand color={colors.gray00} marginX="none" maxWidth="xxl">
        {isMobile ? (
          <div css={[cssNavBarLeftMargin, isLcrDashboard && cssDefaultCursor]}>
            <LcrNavBarItemMobile
              isLcrDashboard={isLcrDashboard}
              templeSubMenuItems={templeSubMenuItems}
              menuOptions={menuOptions}
            />
          </div>
        ) : (
          <Row gutters="none" wrap={false}>
            <span css={cssNavBarLeftMargin} />
            <PlaceHome alignY="middle" />
            {menuOptions.map((menuOptionProps) => (
              <LcrNavBarItem
                key={menuOptionProps.menuText}
                menuText={menuOptionProps.menuText}
                showGlowMenu={isLcrDashboard && menuOptionProps.showGlowMenu}
                isLcrDashboard={isLcrDashboard}
                templeSubMenuItems={templeSubMenuItems}
              />
            ))}
          </Row>
        )}

        <div css={cssBottomShadowBorder} />
      </LayoutBand>

      <LayoutBand
        color={colors.gray00}
        marginX={atSize({ default: 'xxs', xs: 'xs', sm: 'sm', md: 'md', lg: 'lg' })}
        maxWidth="md"
      >
        <Separator size="md" />
        <FlowGrid columnCount={centerUnitInfo ? 1 : 2} guttersY="xxs">
          <div>
            <div css={cssUberSearchBar}>
              <input
                aria-label={searchFieldPlaceholder}
                css={cssUberSearchTextField}
                onChange={() => {}}
                placeholder={searchFieldPlaceholder}
                value=""
              />
              <Button emphasis="high" Icon={MenuSearch}>
                {searchButtonText}
              </Button>
            </div>
            <RadioGroup dense orientation="horizontal">
              <Radio
                checked={selectedRadio === 'my ward'}
                label={searchRadio1Label}
                onChange={handleRadioChange}
                name="search-scope"
                value="my ward"
              />
              <Radio
                checked={selectedRadio === 'other units'}
                label={searchRadio2Label}
                onChange={handleRadioChange}
                name="search-scope"
                value="other units"
              />
              <NoticeQuestion color={colors.blue60} alignY="middle" />
            </RadioGroup>
          </div>

          <Row alignX={centerUnitInfo ? 'center' : 'end'} wrap>
            <Paragraph centered dense size="sm">
              {stakeUnit}
            </Paragraph>
            <Paragraph centered dense size="sm">
              {branchUnit}
            </Paragraph>
          </Row>
        </FlowGrid>
        <Separator size="lg" />
      </LayoutBand>
    </>
  )
}
